// src/components/ui/Chip.jsx
import React from "react";
import { Badge } from "./Badge";

const cx = (...c) => c.filter(Boolean).join(" ");

/**
 * Chip (Preset 기반)
 *
 * props:
 * - chip: 칩 이름 (디자인 세트)
 * - status: "default" | "selected" | "disabled"
 * - label: 메인 텍스트
 * - badgeLabel: 배지 텍스트
 * - allowBadge: (옵션) true면 해당 칩이 badgeMode="optional"일 때만 badge 사용
 *
 * 내부 규칙:
 * - badgeMode: "none" | "optional"
 *   - none     : 이 칩은 배지 디자인 안 씀 (badgeLabel 무시)
 *   - optional : badgeLabel이 들어오면 표시
 */

const CHIP_PRESET = {
  // AI 추천 옵션 칩 (배지 가능, 기본 패턴)
  a: {
    base: "inline-flex items-center gap-2 px-[12px] py-[8px] rounded-full border",
    status: {
      default:
        "bg-surface-light-white border-border-subtle text-text-disabled text-c2",
      selected:
        "bg-surface-light-primary-light border-border-primary text-text-primary text-c1",
      disabled:
        "bg-surface-light-white border-border-subtle text-text-disabled text-c2", //detault
    },
    badgeMode: "optional",
  },

  b: {
    base: "inline-flex items-center gap-2 h-9 px-[12px] py-[16px] rounded-[8px] border",
    status: {
      default:
        "bg-surface-light-white border-border-subtle text-text-subtle text-c2",
      selected:
        "bg-surface-light-primary-light border-border-primary text-text-primary text-c1",
      disabled:
        "bg-surface-light-white border-border-subtle text-text-subtle text-c2", //detault
    },
  },

  // 일반 칩 (배지 없음, 기본 패턴)
  c: {
    base: "inline-flex items-center gap-2 px-[8px] h-[24px] rounded-full border border-[1px] text-b2",
    status: {
      default:
        "bg-surface-light-primary-light text-text-primary border-border-primary", //detault
      disabled:
      "text-text-disabled border-border-subtle",
    },
    badgeMode: "none",
  },

  // 일반 칩 (배지 없음)
  d: {
    base: "inline-flex items-center gap-1 h-8 px-[12px] rounded-[12px] border",
    status: {
      default:
        "bg-surface-light-white border-border-default text-text-disabled text-c2",
      selected:
        "bg-surface-light-primary-light border-border-primary text-text-primary text-c1", //default
      disabled:
        "bg-surface-light-white border-border-default text-text-disabled text-c2",
    },
    badgeMode: "none",
  },

  // 검정 칩 (배지 없음)
  e: {
    base: "inline-flex items-center justify-center gap-1 h-[22px] min-w-[40px] px-[8px] py-[4px] rounded-full border",
    status: {
      default:
        "bg-surface-dark-absolute-dimmed_80 border-white_absolute text-text-white-absolute text-c3",
    },
    badgeMode: "none",
  },

  // ai 자동 생성용
  f: {
    base: "inline-flex items-center gap-1 h-8 px-[12px] rounded-[12px] border",
    status: {
      default:
        "bg-surface-light-white border-border-primary text-text-primary text-c1",
    },
    badgeMode: "none",
  },
};

function resolvePreset(chip, status) {
  const preset = CHIP_PRESET[chip] || CHIP_PRESET.a;
  const statusMap = preset.status || {};

  const statusClass =
    statusMap[status] ??
    statusMap.default ??
    statusMap.selected ??
    statusMap.disabled ??
    "";

  return {
    baseClass: preset.base,
    statusClass,
    badgeMode: preset.badgeMode || "none",
  };
}

export function Chip({
  chip = "a",
  status = "default",
  label,
  badgeLabel,
  allowBadge = true,
  className = "",
  onClick,
  style,
  ...props
}) {
  const isDisabled = status === "disabled";
  const { baseClass, statusClass, badgeMode } = resolvePreset(chip, status);

  const showBadge =
    badgeMode === "optional" && (allowBadge ?? true) && !!badgeLabel && !isDisabled;

  // chip 타입이 'e'일 때만 배경에 80% opacity 적용
  const eBgStyle =
    chip === "e"
      ? { backgroundColor: "rgba(0, 0, 0, 0.8)" } // #000000, 80%
      : {};

  return (
    <button
      type="button"
      disabled={isDisabled}
      onClick={isDisabled ? undefined : onClick}
      className={cx(baseClass, statusClass, className)}
      style={{ ...style, ...eBgStyle }}
      {...props}
    >
      {showBadge && (
        <Badge
          size="xsmall"
          selected={status === "selected"}
          disabled={isDisabled}
          label={badgeLabel}
        >
          {badgeLabel}
        </Badge>
      )}
      <span>{label}</span>
    </button>
  );
}
